import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, categorySoftwareMap } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'system-tools');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const softwareList = categorySoftwareMap[activeSlug] || [];

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '软件分类', path: '/category/system-tools' },
    { label: activeCategory?.name || activeSlug },
  ];

  const pageSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91free.com' },
        { '@type': 'ListItem', position: 2, name: '软件分类', item: 'https://www.91free.com/category/system-tools' },
        { '@type': 'ListItem', position: 3, name: activeCategory?.name || activeSlug, item: `https://www.91free.com/category/${activeSlug}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${activeCategory?.name || ''} - 91免费版软件分类`,
      description: activeCategory?.description || '',
      url: `https://www.91free.com/category/${activeSlug}`,
    },
  ];

  return (
    <>
      <SEOHead
        title={`${activeCategory?.name || '软件分类'} - 免费软件下载`}
        description={activeCategory?.description || '91免费版软件分类页面，按类别浏览海量免费软件资源。'}
        keywords={`${activeCategory?.name || '软件'},免费软件,软件下载,91免费版,免费工具`}
        canonicalUrl={`https://www.91free.com/category/${activeSlug}`}
        ogTitle={`${activeCategory?.name || '软件分类'} - 91免费版免费软件下载`}
        schemaData={pageSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 bg-background-50 min-h-screen">
        <div className="bg-white border-b border-background-200">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />

            <div className="px-4 md:px-6 pb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                {activeCategory?.name} · 免费软件下载
              </h1>
              <p className="text-sm text-foreground-600">
                {activeCategory?.description}
              </p>
            </div>

            <div className="px-4 md:px-6 pb-4 overflow-x-auto">
              <div className="flex gap-2 flex-nowrap min-w-max">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveSlug(cat.slug)}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          {softwareList.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {softwareList.map((sw, index) => (
                <div
                  key={index}
                  className="p-6 rounded-lg bg-white border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className={`${sw.icon} text-xl`} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-foreground-900 text-base truncate">
                        {sw.name}
                      </h3>
                      <div className="flex items-center gap-1 mt-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <i
                            key={star}
                            className={`text-xs ${
                              star <= Math.floor(sw.rating)
                                ? 'ri-star-fill text-yellow-500'
                                : star <= sw.rating
                                  ? 'ri-star-half-fill text-yellow-500'
                                  : 'ri-star-line text-foreground-300'
                            }`}
                          />
                        ))}
                        <span className="text-xs text-foreground-500 ml-1">{sw.rating}</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 mb-4 leading-relaxed line-clamp-2">
                    {sw.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-400">
                      <i className="ri-download-line mr-1" />
                      {sw.downloads}
                    </span>
                    <button className="inline-flex items-center px-4 py-1.5 text-xs font-semibold whitespace-nowrap rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer">
                      免费下载
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-inbox-line text-2xl" />
              </div>
              <p className="text-foreground-500">该分类暂无软件，敬请期待</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}